import {
  Box,
  Button,
  Card,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import RichTextEditorDialog from "src/components/prize-draw-admin/RichTextEditor";
import { getUserPermissions } from "src/utils/permissionsUtils";

export default function PrizeDrawForm({
  prizeDrawBody,
  setPrizeDrawBody,
  handleDateChange,
  handleChangeFeatured,
  handleChangeStatus,
  fileInputRef,
  handleButtonClick,
  uploadFiles,
  handlePrizeChange,
  handlePrizeSubmit,
  handleRemovePrizeRow,
  handleAddPrizeRow,
  loadingDeletePrizeIds,
  prizeDrawParams,
  prizeLoading,
  postalEntryRef,
  tAndCsRef,
  winnersMsgRef,
}) {
  const dateFormat = "YYYY-MM-DD HH:mm:ss";
  const { isViewer } = getUserPermissions();

  return (
    <Card sx={{ p: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label="Draw Date"
              value={moment(prizeDrawBody.drawDate, dateFormat)}
              onChange={(value) => handleDateChange(value, "drawDate")}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={4}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label="Start Date"
              value={moment(prizeDrawBody.submissionStartDate, dateFormat)}
              onChange={(value) =>
                handleDateChange(value, "submissionStartDate")
              }
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={4}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DateTimePicker
              label="End Date"
              value={moment(prizeDrawBody.submissionEndDate, dateFormat)}
              onChange={(value) => handleDateChange(value, "submissionEndDate")}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={3}>
          <Stack direction="row" spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="type-select-label">Featured</InputLabel>
              <Select
                labelId="type-label"
                id="type-select"
                value={prizeDrawBody.isFeatured}
                label="Featured"
                onChange={handleChangeFeatured}
              >
                <MenuItem value={false}>No</MenuItem>
                <MenuItem value={true}>Yes</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Grid>
        <Grid item xs={3}>
          <FormControl fullWidth>
            <InputLabel id="type-select-label">Status</InputLabel>
            <Select
              labelId="type-label"
              id="type-select"
              value={prizeDrawBody.status}
              label="Status"
              onChange={handleChangeStatus}
            >
              <MenuItem disabled={prizeDrawBody.isCompleted} value="draft">
                Draft
              </MenuItem>
              <MenuItem disabled={prizeDrawBody.isCompleted} value="pending">
                Pending
              </MenuItem>
              <MenuItem disabled={prizeDrawBody.isCompleted} value="active">
                Active
              </MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Title"
            fullWidth
            variant="standard"
            value={prizeDrawBody.title}
            onChange={(e) =>
              setPrizeDrawBody({ ...prizeDrawBody, title: e.target.value })
            }
          />
        </Grid>
        <Grid container spacing={2} sx={{ mt: 2, ml: 1 }}>
          <Grid item xs={12} sm={4}>
            <Stack spacing={2}>
              {prizeDrawBody.image ? (
                <img
                  src={prizeDrawBody.image}
                  alt="Prize"
                  style={{
                    width: "237px",
                    height: "237px",
                    borderRadius: "1px",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 237,
                    height: 237,
                    borderRadius: 1,
                    backgroundColor: "#f0f0f0",
                  }}
                />
              )}

              <Button
                onClick={handleButtonClick}
                variant="outlined"
                sx={{ width: "50%" }}
              >
                Upload Image
              </Button>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={fileInputRef}
                onChange={uploadFiles}
              />
            </Stack>
          </Grid>
        </Grid>

        <Grid item xs={10} sx={{ mt: 4, p: 1 }}>
          {prizeDrawBody?.prizeText?.map((row, index) => (
            <Stack direction="row" spacing={5} mb={2} key={row.id}>
              <TextField
                variant="standard"
                fullWidth
                value={row.title}
                placeholder="Enter Prize"
                onChange={(e) => handlePrizeChange(e.target.value, row.id)}
              />
              <Stack
                direction="row"
                spacing={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {prizeDrawParams && (
                  <Button
                    variant="contained"
                    onClick={() => handlePrizeSubmit(row)}
                    disabled={prizeLoading}
                    sx={{
                      height: 30,
                      width: 20,
                      fontSize: 11,
                      padding: 0,
                    }}
                  >
                    Save
                  </Button>
                )}
                {!isViewer && (
                  <IconButton
                    disabled={loadingDeletePrizeIds.includes(row.id)}
                    onClick={() => handleRemovePrizeRow(row.id)}
                  >
                    <img
                      src={"/assets/icons/delete-row.svg"}
                      alt={"/assets/icons/delete-row.svg"}
                      style={{ width: 25, height: 25 }}
                    />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          ))}
          <Stack sx={{ mt: 4 }} direction="row" spacing={1} alignItems="center">
            <img
              src="/assets/icons/plus-circle.svg"
              alt="Add"
              style={{ width: 16, height: 16 }}
            />
            <Button onClick={handleAddPrizeRow} sx={{ p: 0 }}>
              {" "}
              Add New Prize Row
            </Button>
          </Stack>
        </Grid>

        <Grid item xs={12} sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Postal Entry
          </Typography>
          <RichTextEditorDialog
            editorRef={postalEntryRef}
            value={prizeDrawBody.postalEntry}
            onChange={(html) =>
              setPrizeDrawBody((prev) => ({ ...prev, postalEntry: html }))
            }
            fieldName="postalEntry"
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            T&Cs
          </Typography>
          <RichTextEditorDialog
            editorRef={tAndCsRef}
            value={prizeDrawBody.tAndCs}
            onChange={(html) =>
              setPrizeDrawBody((prev) => ({ ...prev, tAndCs: html }))
            }
            fieldName="tAndCs"
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: 4 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Inbox Winners Message
          </Typography>
          <RichTextEditorDialog
            editorRef={winnersMsgRef}
            value={prizeDrawBody.winnersMessage}
            onChange={(html) =>
              setPrizeDrawBody((prev) => ({
                ...prev,
                winnersMessage: html,
              }))
            }
            fieldName="winnersMessage"
          />
        </Grid>
        <Grid item xs={12} sx={{ mt: 4 }}>
          <TextField
            label="Max Ticket Entry per user (optional)"
            fullWidth
            variant="standard"
            value={prizeDrawBody.maxTicketEntry}
            onChange={(e) =>
              setPrizeDrawBody({
                ...prizeDrawBody,
                maxTicketEntry: e.target.value,
              })
            }
          />
        </Grid>
      </Grid>
    </Card>
  );
}
